// ============================================================================
// Envelope signer flow — rebuilt 2026-07-28.
//
// Information architecture, top to bottom:
//   1. Instruction panel + ESIGN/UETA consent gate  (consent-gate.tsx)
//   2. Document with interactive field tabs         (signer-field.tsx)
//   3. Adopt & Sign modal on first field click      (adopt-modal.tsx)
//   4. Sticky action bar: required-remaining counter + Next / Finish
//
// The document is not rendered until consent is given, which is what makes it
// impossible to scroll past the thing you were supposed to do — the failure
// mode of the previous build, where the adopt block sat below a multi-page
// PDF and the field tabs did not exist as interactive elements at all.
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle, CheckCircle2, Loader2, Lock, ShieldCheck,
} from 'lucide-react';
import { EsignError, esignPublic, fetchSigningPdf } from './lib';
import {
  EnvSignerMeta, FieldType, RenderedPage, envSignerConsent,
  envSignerDocumentShown, envSignerSign, fetchEnvelopePdf, renderPdfPages,
} from './envelope-lib';
import { ConsentGate } from './consent-gate';
import { AdoptModal, AdoptedSignature, TYPEFACES } from './adopt-modal';
import { FIELD_LABEL, SignerFieldTab } from './signer-field';
import { FormView } from './form-view';
import { seedSignerValues } from './packet-launch';
import { pageScroller, scrollPageTo, scrollPageTop } from '../../shared/public-page';

type Step = 'gate' | 'verify' | 'sign' | 'done';
type SignerField = EnvSignerMeta['fields'][number];

/** Field kinds that open the Adopt & Sign modal on first click. */
const ADOPTABLE: FieldType[] = ['signature', 'initials'];
/** Field kinds that fill themselves the moment they are clicked. */
const AUTOFILL: FieldType[] = ['date', 'name', 'email'];

/** Unmissable marker so a preview is never mistaken for the real thing. */
function PreviewBanner() {
  return (
    <div className="sticky top-0 z-40 bg-st-warn-solid px-4 py-2 text-center text-sm font-semibold text-st-solid-ink">
      Preview — nothing here is recorded, and no email is sent
    </div>
  );
}

// ── small text-entry modal for free-text fields ─────────────────────────────

function TextFieldModal({
  label, initial, onSave, onClose,
}: {
  label: string;
  initial: string;
  onSave: (v: string) => void;
  onClose: () => void;
}) {
  const [v, setV] = useState(initial);
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    ref.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-tcs-scrim/60 sm:items-center sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className="w-full rounded-t-xl bg-tcs-raised p-5 sm:max-w-sm sm:rounded-xl"
      >
        <label htmlFor="esign-text-field" className="block text-sm font-medium text-tcs-text">
          {label}
        </label>
        <input
          id="esign-text-field"
          ref={ref}
          value={v}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onSave(v); }}
          className="mt-2 min-h-[48px] w-full rounded-md border border-tcs-border px-3 text-base"
        />
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[48px] flex-1 rounded-md border border-tcs-border text-sm font-medium text-tcs-text"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(v)}
            className="min-h-[48px] flex-1 rounded-md bg-tcs-accent-ink text-sm font-semibold text-tcs-accent-on"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ── main flow ───────────────────────────────────────────────────────────────

export function EnvelopeSignerFlow({
  token, meta: initialMeta, preview = false,
}: {
  token: string;
  meta: EnvSignerMeta;
  /**
   * Preview-as-signer (Phase 2.2). Renders THIS component — not a lookalike —
   * so what the sender sees is what the signer gets, including prefilled
   * values. Every network side effect is short-circuited: no consent event,
   * no signature, no audit rows, no email. The PDF is fetched through the
   * authenticated envelope route instead of the signing token.
   */
  preview?: boolean;
}) {
  const [meta, setMeta] = useState(initialMeta);
  const isApprover = meta.recipient_role === 'APPROVER';
  const myFields = meta.fields;

  const [step, setStep] = useState<Step>(() => {
    if (initialMeta.state === 'completed') return 'done';
    if (!initialMeta.consented) return 'gate';
    return initialMeta.verified ? 'sign' : 'verify';
  });

  const [pages, setPages] = useState<RenderedPage[]>([]);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const pageRefs = useRef(new Map<number, HTMLDivElement>());
  const fieldRefs = useRef(new Map<string, HTMLButtonElement>());

  // consent gate
  const [gateBusy, setGateBusy] = useState(false);
  const [gateError, setGateError] = useState<string | null>(null);

  // identity verification (only reached when auth_mode requires an OTP)
  const [otpSent, setOtpSent] = useState(false);
  const [otpBusy, setOtpBusy] = useState(false);
  const [code, setCode] = useState('');
  const [verifyError, setVerifyError] = useState<string | null>(null);

  // signing
  const [adopted, setAdopted] = useState<AdoptedSignature | null>(null);
  const [adoptFor, setAdoptFor] = useState<SignerField | null>(null);
  const [textFor, setTextFor] = useState<SignerField | null>(null);
  const [values, setValues] = useState<Record<string, string | boolean>>({});
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [signBusy, setSignBusy] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);
  const [earlyFinish, setEarlyFinish] = useState(false);
  const [completedAll, setCompletedAll] = useState(false);

  // Form view (one field per screen) vs the document with overlaid fields.
  // Phones start in form view; anything wider starts on the document.
  const [viewMode, setViewMode] = useState<'document' | 'form'>(
    () => (typeof window !== 'undefined' && window.innerWidth < 640
      ? 'form' : 'document'),
  );
  const [formIndex, setFormIndex] = useState(0);

  const senderName = meta.sender_name ?? 'Terminal Cleaning Services';
  const requiredFields = useMemo(
    () => myFields.filter((f) => f.required),
    [myFields],
  );

  // ── prefilled values (AFB-0075 / PR 5) ────────────────────────────────────
  //
  // esign_fields.value is written by the merge resolver before the envelope is
  // ever sent, and this component never read it: `values` started at {} and
  // every render below reads values[f.field_id], so a prefilled value was
  // invisible to the signer. It was also DESTROYED on submit — the sign action
  // writes this map back over the whole row, so a value nobody could see went
  // out blank, and a required text field with nothing in the map is rejected
  // outright by the edge function.
  //
  // Seeded here rather than in useState's initialiser because the fields
  // arrive asynchronously (envelope-preview builds meta from a fetch, and the
  // signer flow re-sets meta after consent/verify), so a one-shot initial value
  // would miss them. seedSignerValues never overwrites a key the signer already
  // holds — including one they deliberately emptied — and returns the SAME
  // object when there is nothing to add, so React bails out of the update and
  // a re-render cannot clobber what they have typed.
  useEffect(() => {
    setValues((prev) => seedSignerValues(prev, myFields));
  }, [myFields]);

  const loadPdf = useCallback(async () => {
    setPdfError(null);
    try {
      const url = preview
        ? await fetchEnvelopePdf(meta.envelope_id)
        : await fetchSigningPdf(token);
      setPdfUrl(url);
      const targetWidth = Math.min(720, window.innerWidth - 24);
      // pdf.js can hang rather than reject — a worker that fails to start
      // leaves the render promise pending forever, and the signer sits on
      // "Loading document…" with no error and nothing to click. Observed in
      // the field 2026-07-28. Race it against a timeout so a stall becomes a
      // visible, retryable failure instead of a dead page.
      const pages = await Promise.race([
        renderPdfPages(url, targetWidth),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('pdf_render_timeout')), 20_000)),
      ]);
      setPages(pages);
      // Only NOW can we honestly say the signer saw the document. Never in
      // preview — a sender looking at their own draft is not a signer view.
      if (!preview && pages.length > 0) {
        void envSignerDocumentShown(token).catch(() => { /* evidence only */ });
      }
    } catch {
      setPdfError('The document could not be loaded.');
    }
  }, [token, preview, meta.envelope_id]);

  // The document is fetched only once the signer is past the consent gate.
  useEffect(() => {
    if (meta.state === 'waiting') return;
    if (step === 'sign' || step === 'done') void loadPdf();
  }, [loadPdf, meta.state, step]);

  // ── field state helpers ───────────────────────────────────────────────────

  const isDone = useCallback((f: SignerField): boolean => {
    const v = values[f.field_id];
    if (v === undefined) return false;
    if (typeof v === 'boolean') return true;
    return v.trim().length > 0;
  }, [values]);

  const remainingRequired = requiredFields.filter((f) => !isDone(f));
  const allRequiredDone = isApprover || remainingRequired.length === 0;
  const doneCount = myFields.filter(isDone).length;

  const focusField = useCallback((f: SignerField) => {
    setCurrentId(f.field_id);
    const el = fieldRefs.current.get(f.field_id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Focus after the smooth scroll has been kicked off so the browser does
      // not fight it with its own focus-scroll.
      window.setTimeout(() => el.focus({ preventScroll: true }), 220);
      return;
    }
    const page = pageRefs.current.get(f.page);
    if (page) {
      const sc = pageScroller();
      const y = page.getBoundingClientRect().top + (sc ? sc.scrollTop : window.scrollY) +
        f.position_y * page.offsetHeight - 160;
      scrollPageTo(y, 'smooth');
    }
  }, []);

  /** Next incomplete REQUIRED field, else next incomplete optional, else null. */
  const nextIncomplete = useCallback((): SignerField | null => {
    return requiredFields.find((f) => !isDone(f))
      ?? myFields.find((f) => !isDone(f))
      ?? null;
  }, [isDone, myFields, requiredFields]);

  const goNext = useCallback(() => {
    const next = nextIncomplete();
    if (next) focusField(next);
  }, [focusField, nextIncomplete]);

  /** Stamp one field from an adopted signature / autofill rule. */
  const stampValue = useCallback((
    f: SignerField, sig: AdoptedSignature | null,
  ): string | boolean | undefined => {
    switch (f.field_type) {
      case 'signature': return sig ? sig.signature : undefined;
      case 'initials': return sig ? sig.initials : undefined;
      case 'name': return sig?.name ?? meta.signer_name ?? '';
      case 'email': return meta.signer_email_masked;
      case 'date': return new Date().toISOString().slice(0, 10);
      default: return undefined;
    }
  }, [meta.signer_email_masked, meta.signer_name]);

  /** Apply an adopted signature to EVERY signature/initials field at once. */
  function applyAdoption(sig: AdoptedSignature, trigger: SignerField | null) {
    setAdopted(sig);
    setValues((prev) => {
      const next = { ...prev };
      for (const f of myFields) {
        if (!ADOPTABLE.includes(f.field_type)) continue;
        const v = stampValue(f, sig);
        if (v !== undefined) next[f.field_id] = v;
      }
      // A name field is part of the same adoption act.
      for (const f of myFields) {
        if (f.field_type === 'name' && next[f.field_id] === undefined) {
          next[f.field_id] = sig.name;
        }
      }
      if (trigger) next[trigger.field_id] = stampValue(trigger, sig) ?? '';
      return next;
    });
    setAdoptFor(null);
    setEarlyFinish(false);
  }

  /** Click handler for a field tab. */
  function activateField(f: SignerField) {
    setCurrentId(f.field_id);
    setEarlyFinish(false);

    if (ADOPTABLE.includes(f.field_type)) {
      if (!adopted) { setAdoptFor(f); return; }
      setValues((v) => ({ ...v, [f.field_id]: stampValue(f, adopted) ?? '' }));
      window.setTimeout(goNext, 260);
      return;
    }
    if (AUTOFILL.includes(f.field_type)) {
      setValues((v) => ({ ...v, [f.field_id]: stampValue(f, adopted) ?? '' }));
      window.setTimeout(goNext, 260);
      return;
    }
    if (f.field_type === 'checkbox') {
      setValues((v) => ({ ...v, [f.field_id]: !(v[f.field_id] === true) }));
      window.setTimeout(goNext, 260);
      return;
    }
    setTextFor(f);
  }

  // ── consent gate ──────────────────────────────────────────────────────────

  async function acceptConsent() {
    if (preview) {
      // No consent event is written for a preview — the audit trail must only
      // ever contain real signer activity.
      setMeta((m) => ({ ...m, consented: true, verified: true }));
      setStep('sign');
      scrollPageTop();
      return;
    }
    setGateBusy(true);
    setGateError(null);
    try {
      await envSignerConsent(token);
      setMeta((m) => ({ ...m, consented: true }));
      setStep(meta.verified ? 'sign' : 'verify');
      scrollPageTop();
    } catch (e) {
      setGateError(
        e instanceof EsignError && e.status === 410
          ? 'This signing link has expired. Ask Terminal Cleaning Services to send a new one.'
          : 'We could not record your agreement. Try again.',
      );
    } finally {
      setGateBusy(false);
    }
  }

  // ── identity verification (OTP) ───────────────────────────────────────────

  async function requestOtp() {
    setOtpBusy(true);
    setVerifyError(null);
    try {
      await esignPublic({ action: 'request_otp', token });
      setOtpSent(true);
    } catch (e) {
      setVerifyError(
        e instanceof EsignError && e.code === 'otp_delivery_unavailable'
          ? 'Code delivery is not available right now. Contact your TCS representative.'
          : 'Could not send the code. Try again.',
      );
    } finally {
      setOtpBusy(false);
    }
  }

  async function verifyOtp() {
    setOtpBusy(true);
    setVerifyError(null);
    try {
      await esignPublic({ action: 'verify_otp', token, code: code.trim() });
      setMeta((m) => ({ ...m, verified: true }));
      setStep('sign');
    } catch (e) {
      if (e instanceof EsignError && e.code === 'wrong_code') {
        setVerifyError('That code is not correct.');
      } else if (e instanceof EsignError && e.code === 'otp_expired') {
        setVerifyError('That code expired — request a new one.');
        setOtpSent(false);
      } else if (e instanceof EsignError && e.code === 'otp_locked') {
        setVerifyError('Too many attempts — request a new code.');
        setOtpSent(false);
      } else {
        setVerifyError('Verification failed. Try again.');
      }
    } finally {
      setOtpBusy(false);
    }
  }

  // ── submit ────────────────────────────────────────────────────────────────

  async function finish() {
    if (!allRequiredDone) {
      setEarlyFinish(true);
      const next = nextIncomplete();
      if (next) focusField(next);
      scrollPageTop('smooth');
      return;
    }
    if (preview) {
      setCompletedAll(false);
      setStep('done');
      scrollPageTop();
      return;
    }
    setSignBusy(true);
    setSignError(null);
    try {
      const payload: Record<string, string | boolean> = {};
      for (const f of myFields) {
        const v = values[f.field_id];
        if (v !== undefined) payload[f.field_id] = v;
      }
      const res = await envSignerSign({
        token,
        signed_name: adopted?.name ?? meta.signer_name ?? undefined,
        consent: true,
        values: payload,
        adoption_method: adopted?.method,
      });
      setCompletedAll(res.completed);
      if (res.completed) { setPdfUrl(null); void loadPdf(); }
      setStep('done');
      scrollPageTop();
    } catch (e) {
      setSignError(
        e instanceof EsignError && e.message ? e.message : 'Signing failed. Try again.',
      );
    } finally {
      setSignBusy(false);
    }
  }

  // ── short-circuit states ──────────────────────────────────────────────────

  if (meta.state === 'waiting') {
    return (
      <main className="mx-auto max-w-2xl p-6 text-center">
        <Lock className="mx-auto h-12 w-12 text-tcs-text-muted" aria-hidden />
        <h1 className="mt-3 text-lg font-semibold text-tcs-text">Not your turn yet</h1>
        <p className="mt-2 text-sm text-tcs-text-muted">
          {meta.title} is being signed in order. You&rsquo;re #{meta.signing_order} —
          we&rsquo;ll email you when it&rsquo;s your turn (group #{meta.current_group ?? '…'} is
          signing now).
        </p>
      </main>
    );
  }

  if (step === 'done' || meta.state === 'completed') {
    const finished = completedAll || meta.state === 'completed';
    return (
      <>
      {preview && <PreviewBanner />}
      <main className="mx-auto max-w-2xl p-6 text-center">
        {preview && (
          <p className="mb-4 text-sm text-tcs-text-muted">
            This is where the signer lands after finishing. Nothing was signed.
          </p>
        )}
        <CheckCircle2 className="mx-auto h-14 w-14 text-st-ok-fg" aria-hidden />
        <h1 className="mt-4 text-xl font-semibold text-tcs-text">
          {finished ? 'Document executed' : isApprover ? 'Approved' : 'Thank you — you’re done'}
        </h1>
        <p className="mt-2 text-sm text-tcs-text-muted">
          {finished
            ? `Your signed copy has been emailed to ${meta.signer_email_masked}. A sealed copy with the Certificate of Completion is on file with Terminal Cleaning Services.`
            : `Your part is complete and has been emailed to ${meta.signer_email_masked}. The next party has been notified; you'll receive the executed copy once everyone has finished.`}
        </p>

        {!isApprover && myFields.length > 0 && (
          <div className="mx-auto mt-6 max-w-sm rounded-md border border-tcs-border bg-tcs-raised p-4 text-left">
            <p className="text-sm font-semibold text-tcs-text">What you completed</p>
            <ul className="mt-2 space-y-1 text-sm text-tcs-text-muted">
              {myFields.map((f) => (
                <li key={f.field_id} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-st-ok-fg" aria-hidden />
                  <span>{FIELD_LABEL[f.field_type]}</span>
                </li>
              ))}
            </ul>
            {adopted && (
              <p className="mt-3 text-xs text-tcs-text-muted">
                Signature adopted by{' '}
                {adopted.method === 'typed' ? 'typing' : adopted.method === 'drawn' ? 'drawing' : 'upload'}.
              </p>
            )}
          </div>
        )}

        {finished && (
          <a
            href={pdfUrl ?? '#'}
            onClick={async (e) => { if (!pdfUrl) { e.preventDefault(); await loadPdf(); } }}
            download={`TCS-${meta.envelope_id}-executed.pdf`}
            className="mt-6 inline-flex min-h-[56px] items-center rounded-md bg-tcs-accent-ink px-6 text-base font-semibold text-tcs-accent-on"
          >
            Download PDF
          </a>
        )}
      </main>
      </>
    );
  }

  // ── 1. instruction panel + consent gate ───────────────────────────────────

  if (step === 'gate') {
    return (
      <>
        {preview && <PreviewBanner />}
        <ConsentGate
        documentTitle={meta.title}
        senderName={senderName}
        noteToSigner={meta.note_to_signer}
        requiredCount={requiredFields.length}
        isApprover={isApprover}
        busy={gateBusy}
        error={gateError}
          onStart={acceptConsent}
        />
      </>
    );
  }

  // ── 2. identity verification (only when an OTP is actually required) ──────

  if (step === 'verify') {
    return (
      <main className="mx-auto max-w-2xl p-4">
        <section className="rounded-lg border border-tcs-border bg-tcs-raised p-5">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-tcs-accent-ink" aria-hidden />
            <h2 className="text-base font-semibold text-tcs-text">Verify your identity</h2>
          </div>
          <p className="mt-2 text-sm text-tcs-text-muted">
            We&rsquo;ll send a 6-digit code to {meta.signer_email_masked} to confirm
            it&rsquo;s you. Then you can open the document.
          </p>
          {!otpSent ? (
            <button
              type="button"
              onClick={requestOtp}
              disabled={otpBusy}
              className="mt-4 min-h-[56px] w-full rounded-md bg-tcs-accent-ink px-4 text-base font-semibold text-tcs-accent-on disabled:opacity-50"
            >
              {otpBusy ? 'Sending…' : 'Email me the code'}
            </button>
          ) : (
            <>
              <label htmlFor="esign-otp" className="mt-4 block text-sm text-tcs-text-muted">
                Enter the 6-digit code
              </label>
              <input
                id="esign-otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="mt-1 min-h-[56px] w-full rounded-md border border-tcs-border px-4 text-center text-2xl tracking-[0.4em]"
              />
              <button
                type="button"
                onClick={verifyOtp}
                disabled={otpBusy || code.length !== 6}
                className="mt-3 min-h-[56px] w-full rounded-md bg-tcs-accent-ink px-4 text-base font-semibold text-tcs-accent-on disabled:opacity-50"
              >
                {otpBusy ? 'Checking…' : 'Verify'}
              </button>
              <button
                type="button"
                onClick={requestOtp}
                disabled={otpBusy}
                className="mt-2 min-h-[44px] w-full text-sm text-tcs-text-muted underline"
              >
                Send a new code
              </button>
            </>
          )}
          {verifyError && (
            <p role="alert" className="mt-3 text-sm text-st-danger-fg">{verifyError}</p>
          )}
        </section>
      </main>
    );
  }

  // ── 3. signing surface ────────────────────────────────────────────────────

  // Form view is the default on a phone, where a letter-size page scaled to
  // fit makes a signature field about a fingernail wide. The document is
  // always one tap away.
  if (viewMode === 'form' && !isApprover && myFields.length > 0) {
    return (
      <>
        {preview && <PreviewBanner />}
        <FormView
          fields={myFields}
          values={values}
          index={Math.min(formIndex, myFields.length - 1)}
          isDone={isDone}
          remainingRequired={remainingRequired.length}
          canFinish={allRequiredDone}
          finishing={signBusy}
          onIndexChange={setFormIndex}
          onActivate={activateField}
          onToggleCheckbox={(f) =>
            setValues((v) => ({ ...v, [f.field_id]: !(v[f.field_id] === true) }))}
          onShowDocument={() => setViewMode('document')}
          onFinish={finish}
        />
        {adoptFor && (
          <AdoptModal
            trigger={adoptFor.field_type === 'initials' ? 'initials' : 'signature'}
            initialName={adopted?.name ?? meta.signer_name ?? ''}
            existing={adopted}
            onAdopt={(sig) => applyAdoption(sig, adoptFor)}
            onClose={() => setAdoptFor(null)}
          />
        )}
        {textFor && (
          <TextFieldModal
            label={FIELD_LABEL[textFor.field_type]}
            initial={String(values[textFor.field_id] ?? '')}
            onSave={(v) => {
              setValues((prev) => ({ ...prev, [textFor.field_id]: v }));
              setTextFor(null);
            }}
            onClose={() => setTextFor(null)}
          />
        )}
        {signError && (
          <p role="alert" className="fixed inset-x-0 bottom-20 mx-4 rounded-md border border-st-danger-border-strong bg-st-danger-bg p-3 text-sm text-st-danger-fg-strong">
            {signError}
          </p>
        )}
      </>
    );
  }

  const started = doneCount > 0;
  const nextLabel = !started
    ? 'Start'
    : remainingRequired.length > 0
      ? 'Next required'
      : 'Next';

  return (
    <>
    {preview && <PreviewBanner />}
    <main className="mx-auto max-w-2xl p-3 pb-36">
      {/* Compact instruction strip — the full panel is behind the consent
          gate, but the signer should never be without guidance on screen. */}
      <div className="mb-3 rounded-md border border-tcs-border bg-tcs-raised p-3">
        {/* A screen reader lands here with no heading otherwise — the page
            title lives in the header band, which is outside this main. */}
        <h1 className="sr-only">
          Sign {meta.title} — {myFields.length} field
          {myFields.length === 1 ? '' : 's'} to complete
        </h1>
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm text-tcs-text">
            {isApprover
              ? <>Read {meta.title}, then choose <strong>Approve document</strong>.</>
              : <>Tap each highlighted field to complete it. Fields marked{' '}
                  <span className="font-bold text-st-danger-fg">*</span> are required.</>}
          </p>
          {!isApprover && myFields.length > 0 && (
            <button
              type="button"
              onClick={() => setViewMode('form')}
              className="shrink-0 whitespace-nowrap text-sm font-medium text-tcs-accent-ink underline"
            >
              One at a time
            </button>
          )}
        </div>
        {meta.note_to_signer && (
          <div className="mt-2 rounded-md border-l-4 border-tcs-accent-ink bg-[#F4F7FB] px-3 py-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">
              Note from {senderName}
            </p>
            <p className="mt-0.5 whitespace-pre-wrap text-sm text-tcs-text">
              {meta.note_to_signer}
            </p>
          </div>
        )}
      </div>

      {earlyFinish && (
        <div
          role="alert"
          className="mb-3 flex items-start gap-2 rounded-md border border-st-danger-border-strong bg-st-danger-bg p-3"
        >
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-st-danger-fg" aria-hidden />
          <p className="text-sm text-st-danger-fg-strong">
            <strong>
              {remainingRequired.length} required field
              {remainingRequired.length === 1 ? '' : 's'} still to complete.
            </strong>{' '}
            Choose <em>Next required</em> to jump to the next one.
          </p>
        </div>
      )}

      {signError && (
        <p role="alert" className="mb-3 rounded-md border border-st-danger-border-strong bg-st-danger-bg p-3 text-sm text-st-danger-fg-strong">
          {signError}
        </p>
      )}

      <section aria-label="Document">
        {pdfError && (
          <div
            role="alert"
            className="rounded-md border border-st-danger-border-strong bg-st-danger-bg p-4 text-sm text-st-danger-fg-strong"
          >
            <p className="font-medium">{pdfError}</p>
            <p className="mt-1 text-st-danger-fg">
              Nothing has been signed. Try again, or reply to the email that
              sent you here and we&rsquo;ll sort it out.
            </p>
            <button
              type="button"
              onClick={() => { setPages([]); void loadPdf(); }}
              className="mt-3 min-h-[44px] rounded-md bg-tcs-accent-ink px-4 text-sm font-semibold text-tcs-accent-on"
            >
              Try again
            </button>
          </div>
        )}
        {pages.length === 0 && !pdfError && (
          <div className="flex h-64 items-center justify-center text-tcs-text-muted">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden /> Loading document…
          </div>
        )}
        {pages.map((p) => (
          <div
            key={p.pageNumber}
            ref={(el) => { if (el) pageRefs.current.set(p.pageNumber, el); }}
            className="relative mx-auto mb-3 border border-tcs-border bg-tcs-raised"
            style={{ width: p.width, height: p.height }}
          >
            <div
              ref={(el) => {
                if (el && p.canvas.parentElement !== el) {
                  el.innerHTML = '';
                  el.appendChild(p.canvas);
                }
              }}
              aria-hidden
            />
            {!isApprover && myFields
              .map((f, i) => ({ f, i }))
              .filter(({ f }) => f.page === p.pageNumber)
              .map(({ f, i }) => {
                const v = values[f.field_id];
                return (
                  <SignerFieldTab
                    key={f.field_id}
                    ref={(el) => {
                      if (el) fieldRefs.current.set(f.field_id, el);
                      else fieldRefs.current.delete(f.field_id);
                    }}
                    fieldType={f.field_type}
                    required={f.required}
                    done={isDone(f)}
                    isCurrent={currentId === f.field_id}
                    value={v ?? ''}
                    index={i + 1}
                    total={myFields.length}
                    onActivate={() => activateField(f)}
                    style={{
                      left: f.position_x * p.width,
                      top: f.position_y * p.height,
                      // 44px / 88px floors keep every tab a legitimate touch
                      // target even when the placed field is tiny.
                      width: Math.max(f.width * p.width, 88),
                      height: Math.max(f.height * p.height, 44),
                    }}
                  />
                );
              })}
          </div>
        ))}
      </section>

      {/* Adopted-signature reminder, so the signer can change their mind
          without hunting for where they set it. */}
      {adopted && !isApprover && (
        <div className="mt-2 flex items-center justify-between gap-3 rounded-md border border-tcs-border bg-tcs-raised px-3 py-2">
          <span className="min-w-0 truncate text-sm text-tcs-text-muted">
            Signing as{' '}
            <span className={`text-base text-tcs-text ${TYPEFACES[adopted.typeface].className}`}>
              {adopted.name}
            </span>
          </span>
          <button
            type="button"
            onClick={() => setAdoptFor(myFields.find((f) => ADOPTABLE.includes(f.field_type)) ?? null)}
            className="min-h-[44px] shrink-0 text-sm text-tcs-accent-ink underline"
          >
            Change
          </button>
        </div>
      )}

      {/* ── sticky action bar ─────────────────────────────────────────────── */}
      <div className="fixed inset-x-0 bottom-0 border-t border-tcs-border bg-tcs-raised p-3 shadow-[0_-2px_8px_rgba(0,0,0,0.08)]">
        <div className="mx-auto flex max-w-2xl items-center gap-3">
          {!isApprover && (
            <div className="min-w-0 flex-1">
              <p aria-live="polite" className="text-xs font-medium text-tcs-text">
                {remainingRequired.length === 0
                  ? 'All required fields complete'
                  : `${remainingRequired.length} of ${requiredFields.length} required field${requiredFields.length === 1 ? '' : 's'} remaining`}
              </p>
              <div
                role="progressbar"
                aria-label="Required fields completed"
                aria-valuemin={0}
                aria-valuemax={requiredFields.length}
                aria-valuenow={requiredFields.length - remainingRequired.length}
                className="mt-1 h-1.5 overflow-hidden rounded bg-tcs-border"
              >
                <div
                  className="h-full rounded bg-tcs-accent-ink transition-all"
                  style={{
                    width: `${requiredFields.length
                      ? ((requiredFields.length - remainingRequired.length) / requiredFields.length) * 100
                      : 100}%`,
                  }}
                />
              </div>
            </div>
          )}
          {!isApprover && myFields.some((f) => !isDone(f)) && (
            <button
              type="button"
              onClick={goNext}
              aria-label={
                remainingRequired.length > 0
                  ? 'Go to the next required field'
                  : 'Go to the next field'
              }
              className="min-h-[48px] shrink-0 rounded-md border border-tcs-border px-4 text-sm font-medium text-tcs-text focus:outline-none focus-visible:ring-4 focus-visible:ring-tcs-accent-ink/40"
            >
              {nextLabel}
            </button>
          )}
          <button
            type="button"
            onClick={finish}
            disabled={signBusy}
            aria-disabled={!allRequiredDone}
            title={allRequiredDone ? undefined : 'Complete every required field first'}
            aria-describedby={allRequiredDone ? undefined : 'esign-finish-help'}
            className={`min-h-[48px] shrink-0 rounded-md px-5 text-sm font-semibold text-tcs-accent-on focus:outline-none focus-visible:ring-4 focus-visible:ring-tcs-accent-ink/40 ${
              allRequiredDone ? 'bg-tcs-accent-ink' : 'bg-tcs-accent-ink/40'
            }`}
          >
            {signBusy ? 'Sealing…' : isApprover ? 'Approve document' : 'Finish'}
          </button>
        </div>
        {!allRequiredDone && !isApprover && (
          <p
            id="esign-finish-help"
            className="mx-auto mt-1 max-w-2xl text-center text-xs text-tcs-text-muted"
          >
            Finish unlocks once every required field is complete.
          </p>
        )}
      </div>

      {adoptFor && (
        <AdoptModal
          trigger={adoptFor.field_type === 'initials' ? 'initials' : 'signature'}
          initialName={adopted?.name ?? meta.signer_name ?? ''}
          existing={adopted}
          onAdopt={(sig) => applyAdoption(sig, adoptFor)}
          onClose={() => setAdoptFor(null)}
        />
      )}

      {textFor && (
        <TextFieldModal
          label={FIELD_LABEL[textFor.field_type]}
          initial={String(values[textFor.field_id] ?? '')}
          onSave={(v) => {
            setValues((prev) => ({ ...prev, [textFor.field_id]: v }));
            setTextFor(null);
            window.setTimeout(goNext, 260);
          }}
          onClose={() => setTextFor(null)}
        />
      )}
    </main>
    </>
  );
}
